import React, { useState, useEffect, useRef } from 'react';
import { fetchResumeData } from '../../../fetchProjects';

const ProfessionalSummary = () => {
  const [summaries, setSummaries] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await fetchResumeData();
        const filtered = data.filter(item => item.type === 'summary');
        if (filtered.length > 0) {
          setSummaries(filtered);
        }
      } catch (err) {
        console.error('Failed to fetch summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  if (!loading && summaries.length === 0) return null;

  const visibleSummaries = showAll ? summaries : summaries.slice(0, 1);

  const handleToggle = () => {
    if (showAll && sectionRef.current) {
      // Calculate offset for fixed header
      const yOffset = -100; // Adjust this value based on your header height
      const y = sectionRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setShowAll(!showAll);
  };

  return (
    <section ref={sectionRef} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30"></div>
      <div className="relative z-10 border-l-4 border-accent pl-6">
        {loading ? (
          <div className="animate-pulse space-y-4">
             <div className="h-8 bg-white/10 rounded w-3/4"></div>
             <div className="h-6 bg-accent/20 rounded w-1/4 mb-4"></div>
             <div className="h-24 bg-white/5 rounded w-full mb-6"></div>
             <div className="space-y-2">
                <div className="h-4 bg-white/5 rounded w-5/6"></div>
                <div className="h-4 bg-white/5 rounded w-4/6"></div>
                <div className="h-4 bg-white/5 rounded w-full"></div>
             </div>
          </div>
        ) : (
          <>
            {visibleSummaries.map((summaryData, idx) => (
              <div key={idx} className={idx > 0 ? "mt-8 pt-8 border-t border-white/10" : ""}>
                <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-2">
                  {summaryData.title}
                </h2>
                {summaryData.duration && (
                  <p className="text-accent font-semibold text-lg mb-4">
                    {summaryData.duration} of Data-Centric Research Experience
                  </p>
                )}
                <p className="text-text-secondary leading-relaxed mb-6 text-base lg:text-lg">
                  {summaryData.description}
                </p>

                {summaryData.achievements && summaryData.achievements.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
                      Key Highlights
                    </h3>
                    {summaryData.achievements.map((highlight, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0 animate-pulse shadow-[0_0_8px_rgba(14,165,233,0.5)]"></div>
                        <p className="text-text-secondary leading-relaxed">{highlight}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {summaries.length > 1 && (
              <div className="mt-6 pt-4 border-t border-white/10 flex justify-center">
                <button 
                  onClick={handleToggle}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent hover:text-accent-light text-sm font-semibold flex items-center gap-2 transition-all duration-300"
                >
                  {showAll ? 'Show Less' : `Read More (${summaries.length - 1} More)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProfessionalSummary;
